import React, { useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { styled } from "@mui/material/styles";

import {
  ArrowBackRounded,
  CalendarMonthRounded,
  CheckCircleRounded,
  LocalShippingOutlined,
  LocationOnOutlined,
  PaymentsOutlined,
  ShoppingBagOutlined,
  ScheduleRounded,
  ShieldOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";

import {
  LocalizationProvider,
  DateCalendar,
  PickersDay,
} from "@mui/x-date-pickers";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import axios from "axios";
import Payment from "./Payment";

/* =========================================================
   COLORS
========================================================= */

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
  success: "#3F7D4C",
};

/* =========================================================
   STYLED COMPONENTS
========================================================= */

const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: 1180,
  margin: "0 auto",
  padding: theme.spacing(2, 2, 6),

  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(3, 3, 6),
  },
}));

const MainCard = styled(Paper)(() => ({
  borderRadius: 0,
  border: `1px solid ${COLORS.border}`,
  backgroundColor: COLORS.white,
  boxShadow: "none",
  overflow: "hidden",
}));

const SectionCard = styled(Box)(() => ({
  padding: 24,
  borderRadius: 0,
  backgroundColor: COLORS.softCream,
  border: `1px solid ${COLORS.border}`,

  "@media (max-width:600px)": {
    padding: 16,
  },
}));

const PremiumTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: COLORS.white,
    borderRadius: 0,
    fontSize: "0.9rem",
    minHeight: 50,

    "& fieldset": {
      borderColor: COLORS.border,
    },

    "&:hover fieldset": {
      borderColor: COLORS.lightGrey,
    },

    "&.Mui-focused fieldset": {
      borderColor: COLORS.black,
      borderWidth: 1,
    },
  },

  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    color: COLORS.grey,
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: COLORS.black,
  },
}));

const PrimaryButton = styled(Button)(() => ({
  minHeight: 52,
  borderRadius: 0,
  padding: "12px 24px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontSize: "0.68rem",
  backgroundColor: COLORS.black,
  color: COLORS.white,
  boxShadow: "none",

  "&:hover": {
    backgroundColor: COLORS.gold,
    boxShadow: "none",
  },

  "&:disabled": {
    backgroundColor: "#B9B4AC",
    color: COLORS.white,
  },
}));

const SecondaryButton = styled(Button)(() => ({
  minHeight: 52,
  borderRadius: 0,
  padding: "12px 22px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  fontSize: "0.68rem",
  color: COLORS.black,
  borderColor: COLORS.black,

  "&:hover": {
    borderColor: COLORS.gold,
    color: COLORS.gold,
    backgroundColor: COLORS.softCream,
  },
}));

const TimeButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ selected }) => ({
  minHeight: 48,
  borderRadius: 0,
  border: `1px solid ${selected ? COLORS.black : COLORS.border
    }`,
  backgroundColor: selected
    ? COLORS.black
    : COLORS.white,
  color: selected
    ? COLORS.white
    : COLORS.black,
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.03em",
  textTransform: "none",

  "&:hover": {
    backgroundColor: selected
      ? COLORS.gold
      : COLORS.softCream,
    borderColor: selected
      ? COLORS.gold
      : COLORS.black,
  },
}));

/* =========================================================
   HELPERS
========================================================= */

const normalizeDate = (date) => {
  const value = new Date(date);

  return new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate()
  );
};

const formatDate = (date) => {
  const value = normalizeDate(date);

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatApiDate = (date) => {
  if (!date) return "";

  const parts = date.split("-");

  if (parts.length !== 3) return date;

  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const formatDisplayDate = (date) => {
  if (!date) return "";

  const value = new Date(`${date}T00:00:00`);

  if (Number.isNaN(value.getTime())) return date;

  return value.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (amount) => {
  return `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/* =========================================================
   DATE RANGE HELPERS
========================================================= */

const parseDateRange = (range) => {
  if (!range || typeof range !== "string") {
    return null;
  }

  const values = range.split(" - ");

  if (values.length !== 2) {
    return null;
  }

  const parseDate = (value) => {
    const parts = value.trim().split(/\s+/);

    if (parts.length !== 3) {
      return null;
    }

    const day = Number(parts[0]);
    const monthName = parts[1].toLowerCase().slice(0, 3);
    const year = Number(parts[2]);

    const monthMap = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11,
    };

    const month = monthMap[monthName];

    if (
      Number.isNaN(day) ||
      Number.isNaN(year) ||
      month === undefined
    ) {
      return null;
    }

    return new Date(year, month, day);
  };

  const start = parseDate(values[0]);
  const end = parseDate(values[1]);

  if (!start || !end) {
    return null;
  }

  return {
    start: normalizeDate(start),
    end: normalizeDate(end),
  };
};

const generateDatesFromRanges = (ranges = []) => {
  const dates = [];

  ranges.forEach((range) => {
    const parsedRange = parseDateRange(range);

    if (!parsedRange) return;

    const currentDate = new Date(parsedRange.start);

    while (currentDate <= parsedRange.end) {
      dates.push(formatDate(currentDate));

      currentDate.setDate(currentDate.getDate() + 1);
    }
  });

  return [...new Set(dates)];
};

/* =========================================================
   TIME HELPERS
========================================================= */

const generateTimeSlots = (
  startHour = 10,
  endHour = 19,
  interval = 30
) => {
  const slots = [];

  for (
    let minutes = startHour * 60;
    minutes < endHour * 60;
    minutes += interval
  ) {
    const hour24 = Math.floor(minutes / 60);
    const minute = minutes % 60;

    const period = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 || 12;

    slots.push(
      `${hour12}:${String(minute).padStart(2, "0")} ${period}`
    );
  }

  return slots;
};

const normalizeTime = (time) => {
  if (!time) return "";

  return String(time)
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase()
    .replace(/^0(?=\d:)/, "");
};

const formatTimeForComparison = (time) => {
  if (!time) return "";

  const normalized = normalizeTime(time);

  const match = normalized.match(
    /^(\d{1,2}):(\d{2})\s*(AM|PM)$/
  );

  if (!match) {
    return normalized;
  }

  const hour = Number(match[1]);
  const minute = match[2];
  const period = match[3];

  return `${hour}:${minute} ${period}`;
};

const isTimeWithinBookingRange = (
  time,
  startHour = 10,
  endHour = 19
) => {
  if (!time) return false;

  const normalized = normalizeTime(time);

  const match = normalized.match(
    /^(\d{1,2}):(\d{2})\s*(AM|PM)$/
  );

  if (!match) return false;

  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3];

  if (hour === 12) {
    hour = 0;
  }

  if (period === "PM") {
    hour += 12;
  }

  const totalMinutes = hour * 60 + minute;

  return (
    totalMinutes >= startHour * 60 &&
    totalMinutes < endHour * 60
  );
};


function CustomCalendarDay(props) {
  const {
    day,
    availableDates = [],
    bookedDates = [],
    ...other
  } = props;

  const date = day.format("YYYY-MM-DD");

  const isBooked = bookedDates.includes(date);
  const isAvailable = availableDates.includes(date);

  let backgroundColor = "#E0E0E0";
  let textColor = "#77736D";

  if (isBooked) {
    backgroundColor = "#F59E0B";
    textColor = "#FFFFFF";
  } else if (isAvailable) {
    backgroundColor = "#3F7D4C";
    textColor = "#FFFFFF";
  }

  return (
    <PickersDay
      {...other}
      day={day}
      sx={{
        backgroundColor: `${backgroundColor} !important`,
        color: `${textColor} !important`,
        fontWeight: 700,
        borderRadius: "50%",

        "&:hover": {
          backgroundColor: `${backgroundColor} !important`,
        },

        "&.Mui-selected": {
          backgroundColor: "#171717 !important",
          color: "#FFFFFF !important",
        },

        "&.Mui-disabled": {
          opacity: 0.65,
          color: `${textColor} !important`,
        },
      }}
    />
  );
}
/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function BookingForm({
  bookingType = "rental",
  productId,
  productData,
  onBack,
  tryOnDateTimeUrl,
  onTryOnBooking,
}) {
  const isTryOn = bookingType === "tryOn";

  /*
    Supports:
    1. ProductDetail productData
    2. ProductData wrapper
    3. Direct product object
  */

  const product = productData?.ProductData || productData || {};

  const productDetails = product?.product || product;

  const productName =
    productDetails?.title ||
    productDetails?.productName ||
    productDetails?.name ||
    "Selected Product";

  const productImages = Array.isArray(product?.productImage)
    ? product.productImage.filter(Boolean)
    : [];

  const productImage =
    productImages[0] ||
    productDetails?.productImage ||
    productDetails?.image ||
    productDetails?.images?.[0] ||
    "";

  const subtotal = Number(
    productDetails?.rentAmount ||
    productDetails?.rentalPrice ||
    productDetails?.price ||
    0
  );

  const securityAmount = Number(
    productDetails?.securityAmount || 0
  );

  const isSecurityRequired =
    productDetails?.isSecurity === true;



  const [deliveryOption, setDeliveryOption] = useState("pickup");
  const [bookingDate, setBookingDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [address, setAddress] = useState({
    street1: "",
    street2: "",
    city: "",
    state: "",
    country: "india",
    pinCode: "",
  });

  // Availabke date
  const availableDateRanges = useMemo(() => {
    const availableDate =
      product?.slots?.availableDate ||
      productDetails?.slots?.availableDate ||
      product?.availableDate ||
      [];

    if (!availableDate) {
      return [];
    }

    if (Array.isArray(availableDate)) {
      return availableDate;
    }

    return [String(availableDate)];
  }, [product, productDetails]);

  const normalizeBookedDate = (date) => {
    if (!date) return "";

    const value = String(date).trim();

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    // DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
      const [day, month, year] = value.split("-");
      return `${year}-${month}-${day}`;
    }

    // DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [day, month, year] = value.split("/");
      return `${year}-${month}-${day}`;
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return formatDate(parsedDate);
  };

  const bookedDates = useMemo(() => {
    const dates =
      product?.slots?.bookedDate ||
      productDetails?.slots?.bookedDate ||
      product?.bookedDate ||
      [];

    if (!Array.isArray(dates)) {
      return [];
    }

    return [
      ...new Set(
        dates
          .map(normalizeBookedDate)
          .filter(Boolean)
      ),
    ];
  }, [product, productDetails]);

  const availableDates = useMemo(() => {
    return generateDatesFromRanges(availableDateRanges);
  }, [availableDateRanges]);

  const selectableDates = useMemo(() => {
    return availableDates.filter(
      (date) => !bookedDates.includes(date)
    );
  }, [availableDates, bookedDates]);

  const minimumDate = formatDate(new Date());

  const shipping =
    isTryOn || deliveryOption === "pickup"
      ? 0
      : subtotal * 0.025;

  const tax = 0;
  const total = subtotal + shipping + tax;
  const rentalTotal = isSecurityRequired
    ? total + securityAmount
    : total;

  // TimeSlots
  const generatedTimeSlots = useMemo(() => {
    return generateTimeSlots(10, 19, 60);
  }, []);

  const availableTimeSet = useMemo(() => {
    return new Set(
      availableTimes.map((time) =>
        formatTimeForComparison(time)
      )
    );
  }, [availableTimes]);

  const displayTimeSlots = useMemo(() => {
    return generatedTimeSlots.filter((time) =>
      isTimeWithinBookingRange(time, 10, 19)
    );
  }, [generatedTimeSlots]);


  const handleAddressChange = (event) => {
    const { name, value } = event.target;
    setAddress((previous) => ({
      ...previous,
      [name]: value,
    }));
    setError("");
  };

  // Validation
  const isFormComplete = () => {
    const addressComplete =
      bookingDate &&
      address.street1.trim() &&
      address.city.trim() &&
      address.state.trim() &&
      address.country &&
      /^\d{6}$/.test(address.pinCode.trim());

    if (isTryOn) {
      return Boolean(addressComplete && selectedTime);
    }

    return Boolean(addressComplete);
  };

  // Fetch Tryon Time 
  const fetchTryOnTimes = async (date) => {
    if (!date || !productId || !tryOnDateTimeUrl) {
      setError("Try-On API URL or product ID is missing.");
      return;
    }

    setLoadingTimes(true);
    setAvailableTimes([]);
    setSelectedTime("");
    setError("");

    try {
      const formattedDate = formatApiDate(date);

      const response = await axios.get(tryOnDateTimeUrl, {
        headers: {
          "product-id": String(productId),
          "booking-date": formattedDate,
        },
      });

      const bookingTime =
        response?.data?.TryOnTime?.bookingTime;

      const times = Array.isArray(bookingTime)
        ? bookingTime
        : [];

      setAvailableTimes(times);

      if (times.length === 0) {
        setError(
          "No time slots are available for this date."
        );
      }
    } catch (requestError) {
      console.error(
        "Fetch Try-On Times Error:",
        requestError
      );

      setAvailableTimes([]);
      setSelectedTime("");

      if (requestError?.response?.status === 404) {
        setError(
          "No Try-On schedule found for the selected date."
        );
      } else {
        setError(
          requestError?.response?.data?.message ||
          "Unable to load Try-On time slots."
        );
      }
    } finally {
      setLoadingTimes(false);
    }
  };


  const handleDateChange = (event) => {
    const date = event.target.value;
    setBookingDate(date);
    setSelectedTime("");
    setAvailableTimes([]);
    setError("");

    if (!date) {
      return;
    }

    if (availableDates.length > 0 && !selectableDates.includes(date)) {
      setError(
        "The selected date is not available for booking."
      );

      setBookingDate("");
      return;
    }

    if (isTryOn) {
      fetchTryOnTimes(date);
    }
  };

  //Handle Booking 
  const handleTryOnBooking = async () => {
    if (!isFormComplete()) {
      setError("Please complete all required details.");
      return;
    }

    if (!onTryOnBooking) {
      setError(
        "Try-On booking API callback has not been configured."
      );
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      await onTryOnBooking({
        productId,
        bookingType: "tryOn",
        bookingDate,
        bookingTime: selectedTime,
        address,
        deliveryOption: "pickup",
      });

      setSuccessMessage(
        "Your Try-On request has been submitted successfully."
      );
    } catch (requestError) {
      console.error(
        "Try-On Booking Error:",
        requestError
      );

      setError(
        requestError?.response?.data?.message ||
        "Unable to submit Try-On request."
      );
    } finally {
      setSubmitting(false);
    }
  };


  const handleNextClick = () => {
    if (!isFormComplete()) {
      setError(
        "Please complete all required details correctly."
      );
      return;
    }

    setError("");
    setShowSummary(true);
  };

  const handleCheckout = () => {
    setShowPaymentSection(true);
  };

  if (successMessage) {
    return (
      <PageContainer>
        <MainCard>
          <Box
            sx={{
              p: { xs: 3, md: 6 },
              textAlign: "center",
              backgroundColor: COLORS.white,
            }}
          >
            <CheckCircleRounded
              sx={{
                fontSize: 70,
                color: COLORS.success,
                mb: 2,
              }}
            />

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 28, md: 36 },
                color: COLORS.black,
                mb: 1.5,
              }}
            >
              Request Submitted
            </Typography>

            <Typography
              sx={{
                color: COLORS.grey,
                fontSize: 14,
                lineHeight: 1.8,
                maxWidth: 450,
                mx: "auto",
                mb: 3,
              }}
            >
              {successMessage}
            </Typography>

            <PrimaryButton onClick={onBack}>
              Back to Product
            </PrimaryButton>
          </Box>
        </MainCard>
      </PageContainer>
    );
  }

  /* =========================================================
     PAYMENT VIEW
  ========================================================= */

  if (showPaymentSection && !isTryOn) {
    return (
      <PageContainer>
        <Stack spacing={3}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
          >
            <IconButton
              onClick={() => setShowPaymentSection(false)}
              sx={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 0,
                color: COLORS.black,
              }}
            >
              <ArrowBackRounded />
            </IconButton>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: 28,
                  color: COLORS.black,
                }}
              >
                Secure Payment
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color: COLORS.grey,
                  mt: 0.5,
                }}
              >
                Complete your rental booking
              </Typography>
            </Box>
          </Stack>

          <MainCard>
            <Box sx={{ p: { xs: 2, md: 4 } }}>
              <Payment />
            </Box>
          </MainCard>
        </Stack>
      </PageContainer>
    );
  }

  if (showSummary && !isTryOn) {
    return (
      <PageContainer>
        <Stack spacing={3}>
          <Box>
            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 30, md: 38 },
                color: COLORS.black,
              }}
            >
              Review Your Booking
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                color: COLORS.grey,
                mt: 1,
              }}
            >
              Check your details before continuing.
            </Typography>
          </Box>

          <MainCard>
            <Box sx={{ p: { xs: 2, md: 4 } }}>
              <Stack spacing={3}>
                {/* PRODUCT SUMMARY */}

                <SectionCard>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >
                    {productImage && (
                      <Box
                        component="img"
                        src={productImage}
                        alt={productName}
                        sx={{
                          width: 90,
                          height: 110,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    )}

                    <Box flex={1}>
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontWeight: 700,
                          letterSpacing: "0.16em",
                          textTransform: "uppercase",
                          color: COLORS.gold,
                          mb: 1,
                        }}
                      >
                        Rental Booking
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 22,
                          color: COLORS.black,
                        }}
                      >
                        {productName}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: COLORS.grey,
                          mt: 1,
                        }}
                      >
                        {formatDisplayDate(bookingDate)}
                      </Typography>
                    </Box>
                  </Stack>
                </SectionCard>

                {/* DELIVERY DETAILS */}

                <SectionCard>
                  <SectionHeading>
                    <LocationOnOutlined
                      sx={{ fontSize: 20 }}
                    />
                    Delivery Details
                  </SectionHeading>

                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.8,
                      color: COLORS.grey,
                    }}
                  >
                    {address.street1}
                    {address.street2 &&
                      `, ${address.street2}`}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.8,
                      color: COLORS.grey,
                    }}
                  >
                    {address.city}, {address.state} -{" "}
                    {address.pinCode}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 13,
                      color: COLORS.grey,
                      textTransform: "capitalize",
                    }}
                  >
                    {address.country}
                  </Typography>

                  <Divider
                    sx={{
                      my: 2,
                      borderColor: COLORS.border,
                    }}
                  />

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    {deliveryOption === "pickup" ? (
                      <ShoppingBagOutlined
                        sx={{ color: COLORS.gold }}
                      />
                    ) : (
                      <LocalShippingOutlined
                        sx={{ color: COLORS.gold }}
                      />
                    )}

                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: COLORS.black,
                      }}
                    >
                      {deliveryOption === "pickup"
                        ? "Pickup"
                        : "Standard Delivery"}
                    </Typography>
                  </Stack>
                </SectionCard>

                {/* PAYMENT SUMMARY */}

                <SectionCard>
                  <SectionHeading>
                    <PaymentsOutlined
                      sx={{ fontSize: 20 }}
                    />
                    Payment Summary
                  </SectionHeading>

                  <Stack spacing={2}>
                    <SummaryRow
                      label="Subtotal"
                      value={formatCurrency(subtotal)}
                    />

                    <SummaryRow
                      label="Shipping"
                      value={
                        shipping === 0
                          ? "Free"
                          : formatCurrency(shipping)
                      }
                    />

                    <SummaryRow
                      label="Estimated Tax"
                      value={formatCurrency(tax)}
                    />

                    {isSecurityRequired && (
                      <SummaryRow
                        label="Security Amount"
                        value={formatCurrency(
                          securityAmount
                        )}
                      />
                    )}

                    <Divider
                      sx={{ borderColor: COLORS.border }}
                    />

                    <SummaryRow
                      label="Total Amount"
                      value={formatCurrency(rentalTotal)}
                      strong
                    />
                  </Stack>
                </SectionCard>

                {/* ACTION BUTTONS */}

                <Stack
                  direction={{
                    xs: "column-reverse",
                    sm: "row",
                  }}
                  spacing={2}
                  justifyContent="space-between"
                >
                  <SecondaryButton
                    variant="outlined"
                    onClick={() => setShowSummary(false)}
                  >
                    Edit Details
                  </SecondaryButton>

                  <PrimaryButton
                    onClick={handleCheckout}
                    startIcon={<PaymentsOutlined />}
                  >
                    Continue to Payment
                  </PrimaryButton>
                </Stack>
              </Stack>
            </Box>
          </MainCard>
        </Stack>
      </PageContainer>
    );
  }

  /* =========================================================
     MAIN FORM
  ========================================================= */

  return (
    <PageContainer>
      <Stack spacing={3}>
        {/* HEADER */}

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: COLORS.gold,
                mb: 1,
              }}
            >
              {isTryOn
                ? "Try-On Experience"
                : "Rental Collection"}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 30, md: 38 },
                lineHeight: 1.1,
                color: COLORS.black,
              }}
            >
              {isTryOn
                ? "Book a Try-On"
                : "Complete Your Booking"}
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                color: COLORS.grey,
                mt: 1,
                lineHeight: 1.7,
              }}
            >
              {isTryOn
                ? "Select a date and time for your Try-On."
                : "Enter your details to continue."}
            </Typography>
          </Box>

          <Chip
            label={isTryOn ? "Try-On" : "Rental"}
            sx={{
              borderRadius: 0,
              backgroundColor: COLORS.softCream,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.black,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
            variant="outlined"
          />
        </Stack>

        <MainCard>
          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Stack spacing={4}>
              {/* =================================================
                  PRODUCT
              ================================================= */}

              <SectionCard>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                >
                  {productImage && (
                    <Box
                      component="img"
                      src={productImage}
                      alt={productName}
                      sx={{
                        width: { xs: 78, sm: 100 },
                        height: { xs: 98, sm: 125 },
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  )}

                  <Box flex={1}>
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: "0.17em",
                        textTransform: "uppercase",
                        color: COLORS.gold,
                        mb: 1,
                      }}
                    >
                      Selected Product
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: { xs: 19, sm: 24 },
                        color: COLORS.black,
                      }}
                    >
                      {productName}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 12,
                        color: COLORS.grey,
                        lineHeight: 1.7,
                        mt: 0.8,
                      }}
                    >
                      {isTryOn
                        ? "Choose your preferred Try-On date and time."
                        : "Choose your preferred rental date."}
                    </Typography>
                  </Box>
                </Stack>
              </SectionCard>

              {/* =================================================
                  DATE
              ================================================= */}

              <Box>
                <SectionHeading>
                  <CalendarMonthRounded
                    sx={{ fontSize: 20 }}
                  />

                  {isTryOn
                    ? "Try-On Date"
                    : "Booking Date"}
                </SectionHeading>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box
                    sx={{
                      width: "100%",
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      overflow: "hidden",
                    }}
                  >
                    <DateCalendar
                      value={bookingDate ? dayjs(bookingDate) : null}
                      minDate={dayjs(minimumDate)}
                      onChange={(newValue) => {
                        if (!newValue) return;

                        const selectedDate =
                          newValue.format("YYYY-MM-DD");

                        handleDateChange({
                          target: {
                            value: selectedDate,
                          },
                        });
                      }}
                      slots={{
                        day: CustomCalendarDay,
                      }}
                      slotProps={{
                        day: {
                          availableDates,
                          bookedDates,
                        },
                      }}
                      sx={{
                        width: "100%",
                        maxWidth: 420,
                        margin: "0 auto",

                        "& .MuiPickersCalendarHeader-root": {
                          padding: "0 12px",
                        },

                        "& .MuiDayCalendar-weekDayLabel": {
                          color: COLORS.grey,
                          fontWeight: 700,
                        },

                        "& .MuiPickersDay-root": {
                          margin: "3px",
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                  sx={{ mt: 2, gap: 1.5 }}
                >
                  {[
                    {
                      label: "Available",
                      color: "#3F7D4C",
                    },
                    {
                      label: "Booked",
                      color: "#F59E0B",
                    },
                    {
                      label: "Unavailable",
                      color: "#E0E0E0",
                    },
                  ].map((item) => (
                    <Stack
                      key={item.label}
                      direction="row"
                      spacing={0.8}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          border: `1px solid ${COLORS.border}`,
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: 11,
                          color: COLORS.grey,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                {availableDateRanges.length > 0 && (
                  <Typography
                    sx={{
                      display: "block",
                      mt: 1,
                      fontSize: 11,
                      color: COLORS.grey,
                      lineHeight: 1.7,
                    }}
                  >
                    Available dates:{" "}
                    {availableDateRanges.join(", ")}
                  </Typography>
                )}

                {bookedDates.length > 0 && (
                  <Typography
                    sx={{
                      display: "block",
                      mt: 0.5,
                      fontSize: 11,
                      color: COLORS.grey,
                    }}
                  >
                    Some dates may be unavailable due to
                    existing bookings.
                  </Typography>
                )}
              </Box>

              {/* =================================================
                  TRY-ON TIME
              ================================================= */}

              {isTryOn && bookingDate && (
                <Box>
                  <SectionHeading>
                    <ScheduleRounded
                      sx={{ fontSize: 20 }}
                    />

                    Select Available Time
                  </SectionHeading>

                  {loadingTimes ? (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <CircularProgress
                        size={20}
                        sx={{ color: COLORS.gold }}
                      />

                      <Typography
                        sx={{
                          fontSize: 13,
                          color: COLORS.grey,
                        }}
                      >
                        Loading available time slots...
                      </Typography>
                    </Stack>
                  ) : displayTimeSlots.length === 0 ? (
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: 0,
                        border: `1px solid ${COLORS.border}`,
                        backgroundColor: COLORS.softCream,
                        color: COLORS.black,
                      }}
                    >
                      No time slots are available for
                      this date.
                    </Alert>
                  ) : (
                    <Grid container spacing={1.5}>
                      {displayTimeSlots.map((time) => {
                        const isAvailable = availableTimeSet.has(
                          formatTimeForComparison(time)
                        );

                        const isSelected = selectedTime === time;

                        return (
                          <Grid item xs={6} sm={4} md={3} key={time}>
                            <TimeButton
                              fullWidth
                              selected={isSelected}
                              disabled={isAvailable}
                              onClick={() => {
                                if (isAvailable) return;

                                setSelectedTime(time);
                                setError("");
                              }}
                            >
                              {time}
                            </TimeButton>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Box>
              )}

              {/* =================================================
                  DELIVERY
              ================================================= */}

              {!isTryOn && (
                <Box>
                  <SectionHeading>
                    <LocalShippingOutlined
                      sx={{ fontSize: 20 }}
                    />

                    Delivery Preference
                  </SectionHeading>

                  <RadioGroup
                    value={deliveryOption}
                    onChange={(event) =>
                      setDeliveryOption(event.target.value)
                    }
                  >
                    <Grid container spacing={2}>
                      {/* STANDARD DELIVERY - DISABLED */}
                      <Grid item xs={12} sm={6}>
                        <DeliveryOption
                          value="standard"
                          selected={deliveryOption === "standard"}
                          disabled
                          control={<Radio disabled />}
                          label={
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: COLORS.lightGrey,
                                }}
                              >
                                Standard Delivery
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: COLORS.lightGrey,
                                  mt: 0.5,
                                }}
                              >
                                Currently unavailable
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>

                      {/* PICKUP - ENABLED */}
                      <Grid item xs={12} sm={6}>
                        <DeliveryOption
                          value="pickup"
                          selected={deliveryOption === "pickup"}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: COLORS.black,
                                }}
                              >
                                Pickup
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: COLORS.grey,
                                  mt: 0.5,
                                }}
                              >
                                Collect from the owner
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                </Box>
              )}

              {/* =================================================
                  ADDRESS
              ================================================= */}

              <Box>
                <SectionHeading>
                  <LocationOnOutlined
                    sx={{ fontSize: 20 }}
                  />

                  {isTryOn
                    ? "Your Address"
                    : "Delivery Information"}
                </SectionHeading>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <PremiumTextField
                      fullWidth
                      required
                      label="Street Address Line 1"
                      name="street1"
                      value={address.street1}
                      onChange={handleAddressChange}
                      placeholder="House number and street name"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <PremiumTextField
                      fullWidth
                      label="Street Address Line 2"
                      name="street2"
                      value={address.street2}
                      onChange={handleAddressChange}
                      placeholder="Apartment, landmark, etc."
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      fullWidth
                      required
                      label="City"
                      name="city"
                      value={address.city}
                      onChange={handleAddressChange}
                      placeholder="Enter city"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      fullWidth
                      required
                      label="PIN Code"
                      name="pinCode"
                      value={address.pinCode}
                      onChange={handleAddressChange}
                      placeholder="Enter 6-digit PIN code"
                      inputProps={{
                        maxLength: 6,
                        inputMode: "numeric",
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      fullWidth
                      required
                      label="State"
                      name="state"
                      value={address.state}
                      onChange={handleAddressChange}
                      placeholder="Enter state"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel
                        sx={{
                          fontSize: "0.8rem",
                        }}
                      >
                        Country *
                      </InputLabel>

                      <Select
                        name="country"
                        value={address.country}
                        onChange={handleAddressChange}
                        label="Country *"
                        sx={{
                          backgroundColor: COLORS.white,
                          borderRadius: 0,
                          minHeight: 50,
                          fontSize: "0.9rem",

                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: COLORS.border,
                          },

                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: COLORS.lightGrey,
                          },

                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: COLORS.black,
                          },
                        }}
                      >
                        <MenuItem value="india">
                          India
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              {/* =================================================
                  ERROR
              ================================================= */}

              {error && (
                <Alert
                  severity="warning"
                  sx={{
                    borderRadius: 0,
                    border: `1px solid ${COLORS.border}`,
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* =================================================
                  ACTION BUTTONS
              ================================================= */}

              <Divider
                sx={{
                  borderColor: COLORS.border,
                }}
              />

              <Stack
                direction={{
                  xs: "column-reverse",
                  sm: "row",
                }}
                spacing={2}
                justifyContent="space-between"
              >
                <SecondaryButton
                  variant="outlined"
                  startIcon={<ArrowBackRounded />}
                  onClick={onBack}
                >
                  Back
                </SecondaryButton>

                <PrimaryButton
                  onClick={
                    isTryOn
                      ? handleTryOnBooking
                      : handleNextClick
                  }
                  disabled={
                    !isFormComplete() || submitting
                  }
                >
                  {submitting
                    ? "Submitting..."
                    : isTryOn
                      ? "Send Try-On Request"
                      : "Review Booking"}
                </PrimaryButton>
              </Stack>
            </Stack>
          </Box>
        </MainCard>

        {/* =================================================
            TRUST FEATURES
        ================================================= */}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TrustFeature
              icon={<ShieldOutlined />}
              title="Secure"
              description="Your details are protected."
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TrustFeature
              icon={<LocalShippingOutlined />}
              title="Easy Rental"
              description="Flexible rental experience."
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TrustFeature
              icon={<CalendarMonthRounded />}
              title="Flexible"
              description="Choose your preferred date."
            />
          </Grid>
        </Grid>
      </Stack>
    </PageContainer>
  );
}

/* =========================================================
   SUPPORTING COMPONENTS
========================================================= */

function SectionHeading({ children }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: 2 }}
    >
      {typeof children !== "string" ? (
        children
      ) : (
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: COLORS.black,
          }}
        >
          {children}
        </Typography>
      )}
    </Stack>
  );
}

function DeliveryOption({
  value,
  selected,
  control,
  label,
}) {
  return (
    <Box
      sx={{
        p: 2,
        border: `1px solid ${selected ? COLORS.black : COLORS.border
          }`,
        backgroundColor: selected
          ? COLORS.softCream
          : COLORS.white,
        height: "100%",
      }}
    >
      <FormControlLabel
        value={value}
        control={React.cloneElement(control, {
          sx: {
            color: COLORS.gold,

            "&.Mui-checked": {
              color: COLORS.black,
            },
          },
        })}
        label={label}
        sx={{
          m: 0,
          width: "100%",
          alignItems: "flex-start",

          "& .MuiFormControlLabel-label": {
            flex: 1,
          },
        }}
      />
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  strong = false,
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
    >
      <Typography
        sx={{
          fontSize: strong ? 13 : 12,
          color: strong ? COLORS.black : COLORS.grey,
          fontWeight: strong ? 700 : 500,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: strong ? 14 : 12,
          fontWeight: strong ? 700 : 600,
          color: strong ? COLORS.gold : COLORS.black,
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function TrustFeature({
  icon,
  title,
  description,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderTop: `1px solid ${COLORS.border}`,
        borderBottom: `1px solid ${COLORS.border}`,
        p: 2,
        backgroundColor: COLORS.softCream,
      }}
    >
      <Box
        sx={{
          color: COLORS.gold,
          display: "flex",
          alignItems: "center",
        }}
      >
        {React.cloneElement(icon, {
          sx: { fontSize: 20 },
        })}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: COLORS.black,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 11,
            color: COLORS.grey,
            mt: 0.3,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}